/* =========================================================
   Section: Puddi Hero — Bubble Lifecycle
   Ported from puddi_homepage_fidelity_full_v6.html hero JS.

   How it works:
     Three bubble elements (.bub.b1, .bub.b2, .bub.b3) cycle through
     lines from POOL. Each bubble runs its own async clock, desynced
     from the others by ~1s + jitter so they never enter or leave
     together. No line appears on two bubbles at once.

   Line selection is delegated to window.PuddiShuffle (loaded from
   assets/puddi-shuffle.js via layout/theme.liquid). The deck walks
   a shuffled pass through POOL; when exhausted it reshuffles with
   a seam guard against the last 3 drawn AND any currently-visible
   line, so we never see repeats within the visible three and never
   see a back-to-back repeat at the seam.

   Lifecycle per bubble:
     draw line → set → pop-in (0.55s) → alive (4.2–6.8s with wobble)
     → burst (0.34s) → respawn gap (320–820ms) → repeat

   Starts when .cupzone enters viewport (IntersectionObserver).
   Stops when .cupzone leaves viewport — timers cleared, state reset.

   Reduced-motion fallback: three static bubbles carrying three lines
   from a fresh shuffle of POOL. Different lines every pageview. No
   animation, no lifecycle.

   EDIT IN VS CODE:
     • POOL   — bubble line pool (add/remove/reorder freely)
     • Timing constants at top (POP_ALIVE_MIN/MAX, RESPAWN_MIN/MAX,
       DESYNC_STAGGER, VIEWPORT_THRESHOLD)
   ========================================================= */

(function () {
  'use strict';

  /* ---------- Bubble line pool (edit in VS Code) ---------- */
  /* From Puddi_Asset_Inventory_R3. Target 20–40 lines; keep each ≤ ~8 words. */
  var POOL = [
    "You had me at pudding.",
    "Technically breakfast. Also technically dessert.",
    "Dessert with a day job.",
    "Cold water in. Magic out.",
    "Stir. Set. Spoon. Repeat.",
    "Pudding is a food group. We checked.",
    "Grandma-approved. Kid-approved. Human-approved. Couch-approved.",
    "Your spoon called. It's ready.",
    "Real vanilla beans. Fancy, right?",
    "Eat the whole thing. You know you want to.",
    "Made with real cocoa. And big dreams.",
    "Spoon optional. Spoon recommended.",
    "Small cup. Big mood.",
    "Stirred, not shaken.",
    "Chocolate first. Questions later.",
    "Jiggle responsibly.",
    "Joy, in cup form.",
    "Watch it become pudding.",
    "Less water, thicker pudding. We love science.",
    "Pudding o'clock is real.",
    "Tastes like a tiny victory.",
    "Second helpings encouraged.",
    "Vanilla, but with ambition.",
    "BYO spoon"
  ];

  /* ---------- Timing constants (edit in VS Code) ---------- */
  var POP_ALIVE_MIN     = 3000;  // ms — minimum alive duration - was 4200
  var POP_ALIVE_MAX     = 4800;  // ms — maximum alive duration - was 6800
  var BURST_DURATION    = 360;   // ms — .out animation duration (matches CSS)
  var RESPAWN_MIN       = 320;   // ms — minimum gap between burst and next pop
  var RESPAWN_MAX       = 820;   // ms — maximum gap
  var DESYNC_STAGGER    = 1000;  // ms — base stagger between bubble starts
  var DESYNC_JITTER     = 250;   // ms — additional random jitter on start
  var VIEWPORT_THRESHOLD = 0.15; // fraction of cupzone visible to trigger start

  /* ---------- Reduced-motion detection ---------- */
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Lifecycle controller per hero ---------- */
  function LifecycleHero(root, offset) {
    var bubs = root.querySelectorAll('.bub');
    var onScreen = new Set();
    var timers = [];
    var running = false;

    /* Walking deck — line selection delegated to PuddiShuffle utility.
       Reshuffle seam-guards against the last 3 drawn AND anything
       currently visible on screen. */
    var deck = window.PuddiShuffle.createDeck(POOL, {
      recentSize: 3,
      getVisible: function () { return onScreen; }
    });

    function setLine(b, line) {
      // ":)" becomes a warmly-tinted wink span
      b.querySelector('.txt').innerHTML = line.replace(':)', '<span class="wink">:)</span>');
    }

    function cycle(b, delay) {
      timers.push(setTimeout(function () {
        if (!running) return;
        var line = deck.draw();
        if (!line) return;
        onScreen.add(line);
        setLine(b, line);
        b.classList.remove('out');
        b.classList.add('in');
        var life = POP_ALIVE_MIN + Math.random() * (POP_ALIVE_MAX - POP_ALIVE_MIN);
        timers.push(setTimeout(function () {
          if (!running) return;
          b.classList.remove('in');
          b.classList.add('out');
          onScreen.delete(line);
          timers.push(setTimeout(function () {
            b.classList.remove('out');
            cycle(b, RESPAWN_MIN + Math.random() * (RESPAWN_MAX - RESPAWN_MIN));
          }, BURST_DURATION));
        }, life));
      }, delay));
    }

    this.start = function () {
      if (running || reduced) return;
      running = true;
      bubs.forEach(function (b, i) {
        cycle(b, offset + i * DESYNC_STAGGER + Math.random() * DESYNC_JITTER);
      });
    };

    this.stop = function () {
      running = false;
      timers.forEach(clearTimeout);
      timers = [];
      onScreen.clear();
      bubs.forEach(function (b) { b.classList.remove('in', 'out'); });
    };

    /* Reduced-motion: paint three lines from a fresh shuffle statically.
       Different lines every pageview. No lifecycle. */
    if (reduced) {
      var picks = window.PuddiShuffle.shuffle(POOL).slice(0, 3);
      bubs.forEach(function (b, i) {
        setLine(b, picks[i]);
        b.style.opacity = 1;
        b.style.transform = 'scale(1)';
      });
    }
  }

  /* ---------- Wire hero shell(s) to lifecycle + IntersectionObserver ---------- */
  var heroes = [];
  document.querySelectorAll('[data-hero]').forEach(function (root) {
    var cupzone = root.querySelector('.cupzone');
    if (!cupzone) return;
    var lc = new LifecycleHero(root, 0);
    heroes.push({ el: cupzone, lc: lc });
  });

  if (heroes.length && !reduced) {
    var heroObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var h = heroes.find(function (x) { return x.el === e.target; });
        if (!h) return;
        if (e.isIntersecting) h.lc.start();
        else h.lc.stop();
      });
    }, { threshold: VIEWPORT_THRESHOLD });
    heroes.forEach(function (r) { heroObs.observe(r.el); });
  }
})();
